import { useState, useEffect } from 'react';
import { 
  X, Phone, User, Save, PhoneOutgoing, PhoneOff, PhoneMissed,
  ChevronRight, AlertTriangle, CheckCircle2, Clock, 
  Calendar, ArrowRight, MapPin, Loader2, UserCheck, FileText,
  RefreshCw, MessageSquare, ExternalLink, Info
} from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';
// Import dynamique pour éviter les erreurs de chargement
// import SimulateurMPAWidget from './SuperBatard/widgets/SimulateurMPAWidget';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * Modal de qualification pour appel SORTANT vers un lead
 * Flow:
 * 1. Affichage infos lead + résultat appel (décroché/NRP/injoignable)
 * 2. Si décroché: test éligibilité MPA
 * 3. Finalisation avec statut
 */
const QualificationLeadSortant = ({ 
  isOpen, 
  onClose, 
  lead,        // Le lead depuis la bannière
  authUser, 
  onLeadUpdated,
  onOpenClientFiche,
  onDecroche   // Callback quand le prospect décroche → ouvre QualificationAppel
}) => {
  // États principaux
  // Flow: 'appel' -> 'concerne_adaptation' (si décroché) -> 'eligibilite' ou 'autre_demande' -> 'finalisation'
  const [step, setStep] = useState('appel');
  const [saving, setSaving] = useState(false);
  
  // Résultat de l'appel
  const [resultatAppel, setResultatAppel] = useState(null);
  const [nrpCount, setNrpCount] = useState(lead?.nrp_count || 0);
  
  // Données du simulateur (reçues du widget)
  const [eligibiliteData, setEligibiliteData] = useState(null);
  
  // Statut final et notes
  const [statutFinal, setStatutFinal] = useState(null);
  const [dateRappel, setDateRappel] = useState('');
  const [heureRappel, setHeureRappel] = useState('');
  const [notes, setNotes] = useState('');
  const [dateRDV, setDateRDV] = useState('');
  const [heureRDV, setHeureRDV] = useState('');
  const [autreDemande, setAutreDemande] = useState('');

  // Source du lead
  const [selectedSource, setSelectedSource] = useState('');
  const [sourcesLead, setSourcesLead] = useState([]);

  // Télépro assigné
  const [telepros, setTelepros] = useState([]);
  const [selectedTelepro, setSelectedTelepro] = useState(null);

  // Options de résultat d'appel
  const resultatsAppel = [
    { code: 'DECROCHE', label: 'Décroché', icon: Phone, color: 'emerald', description: 'Le prospect a répondu' },
    { code: 'NRP', label: `NRP ${nrpCount + 1}`, icon: PhoneMissed, color: 'amber', description: 'Ne répond pas' },
    { code: 'INJOIGNABLE', label: 'Faux numéro', icon: PhoneOff, color: 'red', description: 'Numéro inexistant ou hors service' },
    { code: 'RAPPELER', label: 'À rappeler', icon: Clock, color: 'blue', description: 'Demande de rappel ultérieur' },
  ];

  // Statuts finaux possibles
  const statutsFinaux = {
    positifs: [
      { code: 'RDV_R1', label: 'RDV R1 Pris', icon: Calendar, color: 'emerald' },
      { code: 'RAPPEL_PLANIFIE', label: 'Rappel Planifié', icon: Clock, color: 'blue' },
    ],
    negatifs: [
      { code: 'NON_ELIGIBLE', label: 'Non éligible MPA', icon: AlertTriangle, color: 'amber' },
      { code: 'PAS_INTERESSE', label: 'Pas intéressé', icon: X, color: 'red' },
      { code: 'HORS_CIBLE', label: 'Hors cible', icon: MapPin, color: 'slate' },
    ]
  };

  // Reset quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && lead) {
      setStep('appel');
      setResultatAppel(null);
      setNrpCount(lead.nrp_count || 0);
      setEligibiliteData(null);
      setStatutFinal(null);
      setDateRappel('');
      setHeureRappel('');
      setNotes('');
      setDateRDV('');
      setHeureRDV('');
      setAutreDemande('');
      setSelectedSource('');
      setSelectedTelepro(null);
      // Charger les sources et les télépros
      (async () => {
        try {
          const r = await authFetch(`${API}/qualification/statuts`);
          if (r.ok) {
            const data = await r.json();
            setSourcesLead(data.sources_lead || []);
          }
        } catch (e) { console.error('Erreur chargement sources:', e); }
      })();
      (async () => {
        try {
          const r = await authFetch(`${API}/qualification/telepros`);
          if (r.ok) {
            const data = await r.json();
            setTelepros(data.telepros || []);
            // Auto-sélectionner le user connecté
            if (authUser?.email) {
              const match = (data.telepros || []).find(t => t.email === authUser.email);
              if (match) setSelectedTelepro(match);
            }
          }
        } catch (e) { console.error('Erreur chargement telepros:', e); }
      })();
    }
  }, [isOpen, lead]);

  // Gérer le résultat de l'appel
  const handleResultatAppel = async (resultat) => {
    setResultatAppel(resultat);
    
    if (resultat.code === 'DECROCHE') {
      // Si décroché, basculer vers le flow complet QualificationAppel
      if (onDecroche) {
        onDecroche(lead, selectedTelepro);
      } else {
        setStep('concerne_adaptation');
      }
    } else if (resultat.code === 'NRP') {
      // Incrémenter NRP et enregistrer
      await enregistrerNRP();
    } else if (resultat.code === 'RAPPELER') {
      // Afficher les champs de rappel
      setStep('rappel');
    } else if (resultat.code === 'INJOIGNABLE') {
      // Marquer comme injoignable
      await enregistrerInjoignable();
    }
  };

  // Enregistrer un NRP
  const enregistrerNRP = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/webhook/leads/${lead.lead_id}/nrp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name || authUser?.email,
          notes: notes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNrpCount(data.nrp_count);
        
        if (data.nrp_count >= 4) {
          alert(`⚠️ NRP ${data.nrp_count} - Le lead va être marqué comme injoignable après 5 NRP`);
        } else {
          alert(`📞 NRP ${data.nrp_count} enregistré\nLe lead reste dans la bannière pour un prochain appel.`);
        }
        
        if (onLeadUpdated) onLeadUpdated(data);
        onClose();
      }
    } catch (error) {
      console.error('Erreur NRP:', error);
      alert('❌ Erreur lors de l\'enregistrement du NRP');
    } finally {
      setSaving(false);
    }
  };

  // Enregistrer comme injoignable
  const enregistrerInjoignable = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/webhook/leads/${lead.lead_id}/statut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut: 'injoignable',
          etape_pipeline: 'lead_perdu_avant_r1',
          telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name || authUser?.email,
          notes: notes || 'Faux numéro'
        })
      });
      
      if (response.ok) {
        alert('📵 Lead marqué comme faux numéro');
        if (onLeadUpdated) onLeadUpdated({ ...lead, statut: 'injoignable' });
        onClose();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Enregistrer comme "autre demande" (hors périmètre MPA)
  const enregistrerAutreDemande = async () => {
    if (!autreDemande.trim()) {
      alert('⚠️ Veuillez décrire la demande');
      return;
    }
    
    setSaving(true);
    try {
      const response = await authFetch(`${API}/webhook/leads/${lead.lead_id}/statut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut: 'hors_cible',
          etape_pipeline: 'lead_perdu_avant_r1',
          telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name || authUser?.email,
          notes: `Autre demande (hors MPA): ${autreDemande}`,
          source: selectedSource || undefined
        })
      });
      
      if (response.ok) {
        alert('✅ Lead enregistré comme "Autre demande"');
        if (onLeadUpdated) onLeadUpdated({ ...lead, statut: 'hors_cible' });
        onClose();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Enregistrer un rappel
  const enregistrerRappel = async () => {
    if (!dateRappel || !heureRappel) {
      alert('⚠️ Veuillez sélectionner une date et une heure de rappel');
      return;
    }
    
    setSaving(true);
    try {
      const response = await authFetch(`${API}/webhook/leads/${lead.lead_id}/rappel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_rappel: dateRappel,
          heure_rappel: heureRappel,
          telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name || authUser?.email,
          notes: notes
        })
      });
      
      if (response.ok) {
        alert(`📅 Rappel planifié pour le ${dateRappel} à ${heureRappel}`);
        if (onLeadUpdated) onLeadUpdated({ ...lead, statut: 'a_rappeler' });
        onClose();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la planification du rappel');
    } finally {
      setSaving(false);
    }
  };

  // Finaliser avec un statut
  const finaliserLead = async () => {
    if (!statutFinal) {
      alert('⚠️ Veuillez sélectionner un statut final');
      return;
    }
    
    // Vérifications spécifiques
    if (statutFinal.code === 'RDV_R1' && (!dateRDV || !heureRDV)) {
      alert('⚠️ Veuillez sélectionner une date et heure pour le RDV');
      return;
    }
    
    if (statutFinal.code === 'RAPPEL_PLANIFIE' && (!dateRappel || !heureRappel)) {
      alert('⚠️ Veuillez sélectionner une date et heure pour le rappel');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        statut: statutFinal.code.toLowerCase(),
        etape_pipeline: statutFinal.code === 'RDV_R1' ? 'lead_r1_pris' : 
                        statutFinal.code === 'RAPPEL_PLANIFIE' ? 'lead_a_rappeler' :
                        statutFinal.code.startsWith('NON_') || statutFinal.code === 'PAS_INTERESSE' || statutFinal.code === 'HORS_CIBLE' ? 'lead_perdu_avant_r1' : 'lead',
        telepro_email: selectedTelepro?.email || authUser?.email,
        telepro_nom: selectedTelepro?.name || authUser?.name || authUser?.email,
        notes: notes,
        source: selectedSource || undefined,
        eligibilite_data: eligibiliteData,
        date_rdv: dateRDV || null,
        heure_rdv: heureRDV || null,
        date_rappel: dateRappel || null,
        heure_rappel: heureRappel || null
      };
      
      const response = await authFetch(`${API}/webhook/leads/${lead.lead_id}/statut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (statutFinal.code === 'RDV_R1') {
          alert(`✅ RDV R1 pris pour ${lead.prenom} ${lead.nom}\n📅 ${dateRDV} à ${heureRDV}`);
        } else {
          alert(`✅ Lead mis à jour: ${statutFinal.label}`);
        }
        
        if (onLeadUpdated) onLeadUpdated(data);
        onClose();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la finalisation');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <PhoneOutgoing className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Appel Sortant - Qualification Lead</h2>
              <p className="text-sm text-white/70">
                {lead.prenom} {lead.nom} • {lead.telephone?.replace(/(\d{2})(?=\d)/g, '$1 ')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Infos lead */}
        <div className="bg-slate-800/50 border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-slate-300">
              <MapPin className="w-4 h-4 text-blue-400" />
              DPT {lead.departement}
            </span>
            <span className="flex items-center gap-1 text-slate-300">
              <FileText className="w-4 h-4 text-amber-400" />
              {lead.source}
            </span>
            {lead.projet_externe_id && (
              <span className="flex items-center gap-1 text-amber-400">
                <Info className="w-4 h-4" />
                {lead.projet_externe_id}
              </span>
            )}
          </div>
          {nrpCount > 0 && (
            <span className="px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium">
              NRP {nrpCount}
            </span>
          )}
        </div>
        
        {/* Sélecteur Télépro */}
        <div className="bg-blue-500/10 border-b border-slate-700 px-4 py-3" data-testid="telepro-selector-sortant">
          <div className="flex items-center gap-3">
            <UserCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <label className="text-sm text-blue-400 font-medium whitespace-nowrap">Télépro :</label>
            <select
              value={selectedTelepro?.email || ''}
              onChange={e => {
                const tp = telepros.find(t => t.email === e.target.value);
                setSelectedTelepro(tp || null);
              }}
              data-testid="select-telepro-sortant"
              className="flex-1 px-3 py-1.5 bg-slate-900 border border-blue-500/50 rounded-lg text-white text-sm focus:border-blue-400 focus:outline-none"
            >
              <option value="">-- Sélectionner le télépro --</option>
              {telepros.map(tp => (
                <option key={tp.email} value={tp.email}>
                  {tp.name} ({tp.email})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Progress */}
        <div className="h-1 bg-slate-800">
          <div 
            className="h-full bg-teal-500 transition-all duration-300"
            style={{ width: step === 'appel' ? '20%' : 
                          step === 'concerne_adaptation' ? '40%' :
                          step === 'autre_demande' ? '60%' :
                          step === 'eligibilite' || step === 'rappel' ? '60%' : '100%' }}
          />
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* ÉTAPE APPEL: Résultat de l'appel */}
          {step === 'appel' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Résultat de l'appel</h3>
                <p className="text-slate-400">
                  Cliquez sur le numéro pour appeler via Ringover, puis indiquez le résultat
                </p>
              </div>
              
              {/* Numéro cliquable - Déclenche Ringover */}
              <div className="flex justify-center">
                <button 
                  onClick={() => {
                    if (window.ringoverDial && lead.telephone) {
                      window.ringoverDial(lead.telephone);
                    }
                  }}
                  className="flex items-center gap-3 px-8 py-4 bg-emerald-500/20 border-2 border-emerald-500/50 rounded-xl hover:bg-emerald-500/30 hover:scale-105 transition-all group cursor-pointer"
                  title="Cliquer pour appeler avec Ringover"
                >
                  <Phone className="w-8 h-8 text-emerald-400 group-hover:animate-pulse" />
                  <span className="text-2xl font-mono text-emerald-400 tracking-wider">
                    {lead.telephone?.replace(/(\d{2})(?=\d)/g, '$1 ')}
                  </span>
                  <span className="text-emerald-300 text-sm ml-2">Appeler</span>
                </button>
              </div>
              
              {/* Infos complémentaires si dispo */}
              {lead.infos_complementaires && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Informations complémentaires
                  </h4>
                  <p className="text-white">{lead.infos_complementaires}</p>
                </div>
              )}
              
              {/* Boutons résultat */}
              <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                {resultatsAppel.map(resultat => (
                  <button
                    key={resultat.code}
                    onClick={() => handleResultatAppel(resultat)}
                    disabled={saving}
                    className={`p-4 bg-${resultat.color}-500/10 border-2 border-${resultat.color}-500/50 rounded-xl hover:bg-${resultat.color}-500/20 transition-colors disabled:opacity-50 text-left`}
                    style={{
                      backgroundColor: `rgb(var(--${resultat.color}-500) / 0.1)`,
                      borderColor: `rgb(var(--${resultat.color}-500) / 0.5)`
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <resultat.icon className={`w-8 h-8 text-${resultat.color}-400`} 
                        style={{ color: resultat.color === 'emerald' ? '#34d399' : 
                                        resultat.color === 'amber' ? '#fbbf24' :
                                        resultat.color === 'red' ? '#f87171' :
                                        resultat.color === 'blue' ? '#60a5fa' : '#94a3b8' }}
                      />
                      <div>
                        <span className={`text-lg font-bold text-${resultat.color}-400`}
                          style={{ color: resultat.color === 'emerald' ? '#34d399' : 
                                          resultat.color === 'amber' ? '#fbbf24' :
                                          resultat.color === 'red' ? '#f87171' :
                                          resultat.color === 'blue' ? '#60a5fa' : '#94a3b8' }}
                        >
                          {resultat.label}
                        </span>
                        <p className="text-sm text-slate-400">{resultat.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Notes */}
              <div className="max-w-2xl mx-auto">
                <label className="block text-sm text-slate-400 mb-2">Notes (optionnel)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:border-teal-500 focus:outline-none"
                  rows={2}
                  placeholder="Notes sur l'appel..."
                />
              </div>
            </div>
          )}
          
          {/* ÉTAPE CONCERNE ADAPTATION: Question préliminaire */}
          {step === 'concerne_adaptation' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">
                  L'appel concerne-t-il l'adaptation du logement ?
                </h3>
                <p className="text-slate-400">
                  MaPrimeAdapt', aides ANAH, travaux d'accessibilité...
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
                <button
                  onClick={() => setStep('eligibilite')}
                  data-testid="concerne-adaptation-oui"
                  className="p-6 bg-emerald-500/10 border-2 border-emerald-500/50 rounded-xl hover:bg-emerald-500/20 transition-colors"
                >
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <span className="text-lg font-bold text-emerald-400">OUI</span>
                  <p className="text-sm text-slate-400 mt-2">Continuer vers le test d'éligibilité</p>
                </button>
                
                <button
                  onClick={() => setStep('autre_demande')}
                  data-testid="concerne-adaptation-non"
                  className="p-6 bg-amber-500/10 border-2 border-amber-500/50 rounded-xl hover:bg-amber-500/20 transition-colors"
                >
                  <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <span className="text-lg font-bold text-amber-400">NON</span>
                  <p className="text-sm text-slate-400 mt-2">La demande concerne autre chose</p>
                </button>
              </div>
              
              {/* Bouton retour */}
              <div className="flex justify-center">
                <button
                  onClick={() => setStep('appel')}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                >
                  ← Retour au résultat d'appel
                </button>
              </div>
            </div>
          )}
          
          {/* ÉTAPE AUTRE DEMANDE: La demande concerne autre chose */}
          {step === 'autre_demande' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">La demande concerne autre chose</h3>
                <p className="text-slate-400">
                  Enregistrez la demande de {lead.prenom} {lead.nom}
                </p>
              </div>
              
              {/* Source du lead */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 max-w-2xl mx-auto">
                <label className="block text-sm text-slate-400 mb-2 font-medium">Source du lead</label>
                <select
                  value={selectedSource}
                  onChange={e => setSelectedSource(e.target.value)}
                  data-testid="select-source-lead-sortant-autre"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-teal-500 focus:outline-none"
                >
                  <option value="">-- Selectionner la source --</option>
                  {sourcesLead.map(s => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Description de la demande */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 max-w-2xl mx-auto">
                <label className="block text-sm text-amber-400 mb-2 font-medium">Description de la demande *</label>
                <textarea
                  value={autreDemande}
                  onChange={e => setAutreDemande(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                  rows={3}
                  placeholder="Ex: Le client demande des informations sur les panneaux solaires, souhaite un devis pour une chaudière..."
                />
              </div>
              
              {/* Boutons d'action */}
              <div className="flex gap-4 max-w-2xl mx-auto">
                <button
                  onClick={() => setStep('concerne_adaptation')}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                >
                  ← Retour
                </button>
                <button
                  onClick={enregistrerAutreDemande}
                  disabled={saving || !autreDemande.trim()}
                  className="flex-1 px-6 py-3 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  Enregistrer et fermer
                </button>
              </div>
              
              {/* Option pour continuer vers MPA */}
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl max-w-2xl mx-auto">
                <p className="text-sm text-slate-400 mb-3">Le client est aussi intéressé par l'adaptation du logement ?</p>
                <button
                  onClick={() => setStep('eligibilite')}
                  className="w-full px-4 py-3 bg-emerald-600/20 border border-emerald-500/50 text-emerald-400 rounded-lg hover:bg-emerald-600/30 flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Continuer vers le test d'éligibilité
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* ÉTAPE RAPPEL: Planifier un rappel */}
          {step === 'rappel' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Planifier un rappel</h3>
                <p className="text-slate-400">
                  Choisissez quand rappeler {lead.prenom} {lead.nom}
                </p>
              </div>
              
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Date de rappel *</label>
                  <input
                    type="date"
                    value={dateRappel}
                    onChange={e => setDateRappel(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Heure de rappel *</label>
                  <input
                    type="time"
                    value={heureRappel}
                    onChange={e => setHeureRappel(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Motif du rappel</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    rows={2}
                    placeholder="Ex: En déplacement, rappeler après 18h..."
                  />
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setStep('appel')}
                    className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                  >
                    ← Retour
                  </button>
                  <button
                    onClick={enregistrerRappel}
                    disabled={saving || !dateRappel || !heureRappel}
                    className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calendar className="w-5 h-5" />}
                    Planifier le rappel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* ÉTAPE ÉLIGIBILITÉ: Test MPA */}
          {step === 'eligibilite' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Test d'éligibilité MPA</h3>
                <p className="text-slate-400">
                  Vérifiez l'éligibilité de {lead.prenom} {lead.nom} à MaPrimeAdapt'
                </p>
              </div>
              
              {/* Source du lead */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 max-w-2xl mx-auto">
                <label className="block text-sm text-slate-400 mb-2 font-medium">Source du lead</label>
                <select
                  value={selectedSource}
                  onChange={e => setSelectedSource(e.target.value)}
                  data-testid="select-source-lead-sortant"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-teal-500 focus:outline-none"
                >
                  <option value="">-- Selectionner la source --</option>
                  {sourcesLead.map(s => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Placeholder pour le test d'éligibilité - à améliorer avec le widget */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-300 text-center">
                  Le test d'éligibilité MPA sera effectué lors de l'appel.
                  <br />
                  <span className="text-slate-500 text-sm">
                    Utilisez le simulateur dans la fiche client après création.
                  </span>
                </p>
              </div>
              
              {/* Boutons navigation */}
              <div className="flex gap-4">
                <button
                  onClick={() => setStep('appel')}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                >
                  ← Retour
                </button>
                <button
                  onClick={() => setStep('finalisation')}
                  className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  Continuer vers la finalisation
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* ÉTAPE FINALISATION */}
          {step === 'finalisation' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Finalisation</h3>
                <p className="text-slate-400">
                  Choisissez le statut final pour ce lead
                </p>
              </div>
              
              {/* Résumé éligibilité */}
              {eligibiliteData?.checked && (
                <div className={`p-4 rounded-xl border ${
                  eligibiliteData.eligible 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : 'bg-amber-500/10 border-amber-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {eligibiliteData.eligible ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <span className={`font-bold ${eligibiliteData.eligible ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {eligibiliteData.eligible ? 'Éligible MPA' : 'Non éligible MPA'}
                    </span>
                    {eligibiliteData.categorie && (
                      <span className="text-sm text-slate-400">
                        ({eligibiliteData.categorie})
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Statuts positifs */}
              <div>
                <h4 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Statuts positifs
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {statutsFinaux.positifs.map(statut => (
                    <button
                      key={statut.code}
                      onClick={() => setStatutFinal(statut)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        statutFinal?.code === statut.code
                          ? 'bg-emerald-500/20 border-emerald-500'
                          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <statut.icon className={`w-6 h-6 ${
                          statutFinal?.code === statut.code ? 'text-emerald-400' : 'text-slate-400'
                        }`} />
                        <span className={`font-medium ${
                          statutFinal?.code === statut.code ? 'text-emerald-400' : 'text-white'
                        }`}>
                          {statut.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Champs RDV si sélectionné */}
              {statutFinal?.code === 'RDV_R1' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-4">
                  <h4 className="text-emerald-400 font-medium">Date et heure du RDV R1</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Date *</label>
                      <input
                        type="date"
                        value={dateRDV}
                        onChange={e => setDateRDV(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Heure *</label>
                      <input
                        type="time"
                        value={heureRDV}
                        onChange={e => setHeureRDV(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Champs Rappel si sélectionné */}
              {statutFinal?.code === 'RAPPEL_PLANIFIE' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 space-y-4">
                  <h4 className="text-blue-400 font-medium">Date et heure du rappel</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Date *</label>
                      <input
                        type="date"
                        value={dateRappel}
                        onChange={e => setDateRappel(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Heure *</label>
                      <input
                        type="time"
                        value={heureRappel}
                        onChange={e => setHeureRappel(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Statuts négatifs */}
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Statuts négatifs
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {statutsFinaux.negatifs.map(statut => (
                    <button
                      key={statut.code}
                      onClick={() => setStatutFinal(statut)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        statutFinal?.code === statut.code
                          ? 'bg-red-500/20 border-red-500'
                          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <statut.icon className={`w-5 h-5 ${
                          statutFinal?.code === statut.code ? 'text-red-400' : 'text-slate-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          statutFinal?.code === statut.code ? 'text-red-400' : 'text-white'
                        }`}>
                          {statut.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:border-teal-500 focus:outline-none"
                  rows={3}
                  placeholder="Notes sur l'appel, informations complémentaires..."
                />
              </div>
              
              {/* Boutons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setStep('eligibilite')}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
                >
                  ← Retour
                </button>
                <button
                  onClick={finaliserLead}
                  disabled={saving || !statutFinal}
                  className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  Finaliser le lead
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualificationLeadSortant;
